import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['button', 'timer', 'cancelButton']

  connect() {
    this.mediaRecorder = null
    this.chunks = []
    this.timerInterval = null
    this.startTime = null
    this.initDB()
  }

  async initDB() {
    const request = indexedDB.open('AudioStore', 1)
    request.onupgradeneeded = (event) => {
      const db = event.target.result
      if (!db.objectStoreNames.contains('recordings')) {
        db.createObjectStore('recordings')
      }
    }
    request.onsuccess = (event) => {
      this.db = event.target.result
    }
    request.onerror = (event) => {
      console.error('IndexedDB error:', event.target.errorCode)
    }
  }

  async startRecording() {
    console.log('▶ startRecording')
    this.startTime = Date.now()

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      this.mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' })
      this.chunks = []
      this.mediaRecorder.ondataavailable = (e) => {
        this.chunks.push(e.data)
      }

      this.mediaRecorder.onstop = async () => {
        const blob = new Blob(this.chunks, { type: 'audio/webm' })
        this.chunks = []
        try {
          const formData = new FormData()
          formData.append('audio', blob, 'recording.webm')
          formData.append('conversation_id', this.element.dataset.conversationId)
          const response = await fetch('/chat_manager/send_audio', {
            method: 'POST',
            headers: {
              'X-CSRF-Token': document.querySelector("meta[name='csrf-token']").content,
            },
            body: formData,
          })
          if (!response.ok) throw new Error('Failed to send audio')
          console.log('Audio sent!')
        } catch (error) {
          console.error('Failed to send audio')
        }
        await this.saveAudioToIndexedDB(blob)
        console.log('Audio saved to IndexedDB')
      }
      this.mediaRecorder.start()
    } catch (error) {
      console.error('Microphone access denied or error.')
      return
    }

    this.timerTarget.classList.remove('hidden')
    this.cancelButtonTarget.classList.remove('hidden')
    this.buttonTarget.classList.remove('bg-transparent', 'text-gray-700')
    this.buttonTarget.classList.add('bg-red-500', 'text-white', 'animate-pulse', 'shadow-lg')
    this.updateTimer()
    this.timerInterval = setInterval(() => this.updateTimer(), 1000)
  }

  stopRecording() {
    console.log('⏹ stopRecording')
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop()
    }
    this.buttonTarget.classList.remove('bg-red-500', 'text-white', 'animate-pulse', 'shadow-lg')
    this.buttonTarget.classList.add('bg-transparent', 'text-gray-700')
    this.timerTarget.classList.add('hidden')
    this.cancelButtonTarget.classList.add('hidden')
    clearInterval(this.timerInterval)
    this.timerTarget.innerText = ''

    this.mediaRecorder.stream.getTracks().forEach((track) => track.stop())
  }

  cancelRecording() {
    console.log('cancelRecording')

    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.onstop = null
      this.mediaRecorder.stop()
    }

    this.mediaRecorder?.stream.getTracks().forEach((track) => track.stop())
    this.buttonTarget.classList.remove('bg-red-500', 'text-white', 'animate-pulse', 'shadow-lg')
    this.buttonTarget.classList.add('bg-transparent', 'text-gray-700')
    this.timerTarget.classList.add('hidden')
    this.cancelButtonTarget.classList.add('hidden')

    clearInterval(this.timerInterval)
    this.timerTarget.innerText = ''
    this.chunks = []
  }

  toggleRecording() {
    const isRecording = this.buttonTarget.classList.contains('animate-pulse')
    isRecording ? this.stopRecording() : this.startRecording()
  }

  updateTimer() {
    const elapsed = Math.floor((Date.now() - this.startTime) / 1000)
    const minutes = String(Math.floor(elapsed / 60)).padStart(2, '0')
    const seconds = String(elapsed % 60).padStart(2, '0')
    this.timerTarget.innerText = `⏱ ${minutes}:${seconds}`
  }

  saveAudioToIndexedDB(blob) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['recordings'], 'readwrite')
      const store = transaction.objectStore('recordings')
      const request = store.put(blob, 'latest')
      request.onsuccess = () => resolve()
      request.onerror = (event) => reject(event.target.error)
    })
  }

  getAudioFromIndexedDB() {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['recordings'], 'readonly')
      const store = transaction.objectStore('recordings')
      const request = store.get('latest')
      request.onsuccess = () => resolve(request.result)
      request.onerror = (event) => reject(event.target.error)
    })
  }
}
